// ** react imports
import React from 'react';
import PropTypes from 'prop-types';

// ** router imports
import { Link, useNavigate } from 'react-router-dom';

// ** enums imports
import {
  TAX_FILE_TYPE,
  TAX_FILE_STATUS,
  BADGE_THEMES,
} from '../../../enums/common';

// ** components imports
import Badge from '../../../components/common/Badge';

// ** utils imports
import { formatPhoneNumber, formatUtcToLocal } from '../../../utils/Utils';

const TaxFileTableItem = props => {
  // props
  const { data } = props;

  // hooks
  const navigate = useNavigate();

  // Get file status badge (static class mappings for Tailwind)
  const getFileStatusBadge = (fileStatus, isDeleted) => {
    if (isDeleted) {
      return <Badge theme={BADGE_THEMES.DANGER} text="Deleted" size="sm" />;
    }

    const themeMap = {
      [TAX_FILE_STATUS.CREATED]: BADGE_THEMES.CREATED,
      [TAX_FILE_STATUS.IN_PROGRESS]: BADGE_THEMES.IN_PROGRESS,
      [TAX_FILE_STATUS.COMPLETED]: BADGE_THEMES.COMPLETED,
      [TAX_FILE_STATUS.SUBMITTED]: BADGE_THEMES.SUBMITTED,
      [TAX_FILE_STATUS.PROCESSED]: BADGE_THEMES.PROCESSED,
      [TAX_FILE_STATUS.ARCHIVED]: BADGE_THEMES.ARCHIVED,
    };

    const theme = themeMap[fileStatus] || BADGE_THEMES.DEFAULT;
    return <Badge theme={theme} text={fileStatus || 'created'} size="sm" />;
  };

  // Get submission type badge (static class mappings for Tailwind)
  const getSubmissionTypeBadge = submissionType => {
    if (!submissionType) {
      return <Badge theme={BADGE_THEMES.SECONDARY} text="N/A" size="sm" />;
    }

    const isVirtual = submissionType === TAX_FILE_TYPE.VIRTUAL_INTAKE;
    return (
      <Badge
        theme={isVirtual ? BADGE_THEMES.PRIMARY : BADGE_THEMES.WARNING}
        text={isVirtual ? 'Virtual' : 'In Office'}
        size="sm"
      />
    );
  };

  return (
    <tr
      className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer"
      onClick={() =>
        navigate(`/tax-files/details/${data?._id}?type=${data?.submissionType}`)
      }
    >
      {/* Reference Number */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-24">
        <div className="font-medium text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300 hover:underline cursor-pointer">
          #{data?.referenceNumber || 'N/A'}
        </div>
      </td>

      {/* Client Info */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-38">
        <div className="text-gray-800 dark:text-gray-100">
          <div
            className="font-medium truncate"
            title={data?.clientInfo?.fullName || 'N/A'}
          >
            {data?.clientInfo?.fullName || 'N/A'}
          </div>
          <div className="flex flex-col items-start">
            {data?.clientInfo?.phone && (
              <a
                href={`tel:${data.clientInfo.phone}`}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-violet-600 dark:hover:text-violet-400 truncate hover:underline"
                title={formatPhoneNumber(data?.clientInfo?.phone)}
              >
                {formatPhoneNumber(data?.clientInfo?.phone)}
              </a>
            )}
            {data?.clientInfo?.email && (
              <a
                href={`mailto:${data.clientInfo.email}`}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-violet-600 dark:hover:text-violet-400 truncate hover:underline"
                title={data.clientInfo.email}
              >
                {data.clientInfo.email}
              </a>
            )}
          </div>
        </div>
      </td>

      {/* Tax Year */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-20">
        <div className="text-gray-800 dark:text-gray-100">
          {data?.taxYear || 'N/A'}
        </div>
      </td>

      {/* Submission Type */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-24">
        {getSubmissionTypeBadge(data?.submissionType)}
      </td>

      {/* Progress */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="text-gray-80 dark:text-gray-100 w-40">
          <div className="text-sm font-medium">
            {data?.progressDisplay ||
              `${data?.completedSections || 0}/${
                data?.totalSections || 0
              } sections`}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 bg-gray-200 rounded-full h-2 dark:bg-gray-700">
              <div
                className="bg-violet-600 h-2 rounded-full"
                style={{ width: `${data?.overallProgress || 0}%` }}
              ></div>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
              {data?.overallProgress || 0}%
            </div>
          </div>
        </div>
      </td>

      {/* File Status */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-28">
        {getFileStatusBadge(data?.fileStatus, data?.isDeleted)}
      </td>

      {/* Created Date */}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-32">
        <div className="text-gray-800 dark:text-gray-100">
          {formatUtcToLocal(data?.createdAt)}
        </div>
      </td>

      {/* Actions */}
      <td className="whitespace-nowrap w-16">
        <div className="flex items-center-safe justify-center-safe">
          {/* View button */}
          <Link
            className="text-violet-500 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300 uppercase"
            aria-label="View"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
          </Link>
        </div>
      </td>
    </tr>
  );
};

TaxFileTableItem.propTypes = {
  data: PropTypes.object,
  index: PropTypes.number,
};

export default TaxFileTableItem;
